import styled, { css, type DefaultTheme } from 'styled-components';
import { focusRing, evoLoadAnimation, srOnly } from '@/app/components/search/styles/mixins.ts';
import { IconGlyph } from '@/app/common/icons/MaskIcon';

export type PillState = { $selected?: boolean; $enabled?: boolean; $loading?: boolean; $open?: boolean };
type PillProps = PillState & { theme: DefaultTheme };

const getBorderStyles = ({ theme, $selected, $enabled, $loading, $open }: PillProps) => {
  const disabled = $loading || $enabled === false;

  let color = theme.kallisto.color.border.static.borderNeutral;
  if ($selected) color = theme.kallisto.color.border.interactive.borderPrimaryDefault;
  else if ($enabled === false) color = theme.kallisto.color.border.static.borderDisabled;

  let hoverColor = theme.kallisto.color.border.interactive.borderNeutralHover;
  if (disabled) hoverColor = theme.kallisto.color.border.static.borderDisabled;
  else if ($selected) hoverColor = theme.kallisto.color.border.interactive.borderPrimaryDefault;

  return css`
    border: ${$loading ? 'none' : $open ? `1px solid ${hoverColor}` : `1px solid ${color}`};

    /* Outweighs the design system's own pink hover, which it applies to any pill it can click. */
    &:hover {
      border-color: ${disabled ? color : hoverColor};
      box-shadow: ${disabled ? 'none' : `inset 0 0 0 1px ${hoverColor}`};
    }

    ${
      $open &&
      !disabled &&
      css`
        border-color: ${hoverColor};
        box-shadow: inset 0 0 0 1px ${hoverColor};
      `
    }
  `;
};

const getBackgroundStyles = ({ theme, $selected, $enabled, $loading, $open }: PillProps) => {
  const disabled = $loading || $enabled === false;

  let color = theme.kallisto.color.common.white['1000'];
  if ($loading) color = theme.kallisto.color.background.static.backgroundDisabled;
  else if ($selected) color = theme.kallisto.color.background.interactive.backgroundPrimarySelected;

  let hoverColor = null;
  if (!$selected) hoverColor = theme.kallisto.color.background.interactive.backgroundNeutralHover;

  return css`
    background-color: ${$open && hoverColor ? hoverColor : color};

    ${
      $loading &&
      css`
        position: relative;
        ${evoLoadAnimation};
        background-image: linear-gradient(${color}, ${color});
      `
    }

    ${
      !disabled &&
      css`
        &:hover {
          background-color: ${hoverColor};
        }
      `
    }
  `;
};

const getTextStyles = ({ theme, $selected, $enabled, $loading }: PillProps) => {
  const disabled = $loading || $enabled === false;

  let color = theme.kallisto.color.text.static.textPrimary;
  if ($selected) color = theme.kallisto.color.text.interactive.textPrimaryDefault;
  else if (disabled) color = theme.kallisto.color.text.static.textDisabled;

  return css`
    color: ${color};

    > svg {
      fill: ${color};
    }
    [class*='Pill__Label'] span {
      fill: inherit;
    }
    &:hover {
      color: ${color};
      fill: inherit;
    }
  `;
};

// Border, background and text for a pill state — shared with anything pill-like but differently shaped.
export const pillStateStyles = css<PillState>`
  cursor: ${({ $enabled, $loading }) => ($loading || $enabled === false ? 'not-allowed' : 'pointer')};
  ${getBorderStyles}
  ${getBackgroundStyles}
  ${getTextStyles}
`;

const customStylesForSpecificFilterPills = css`
  &.pill-sort {
    > div {
      border-radius: 8px;
    }

    [class*='PillWrapper'] {
      padding: 10px 16px;
    }

    [class*='Pill__IconWrapper'] + [class*='Pill__Label'] {
      margin-left: 8px;
    }

    [class*='Pill__Label'] span {
      padding-left: 0px;
      padding-right: 0px;
      gap: 8px;
    }

    .arrow {
      width: 16px;
      height: 16px;
    }
  }
`;

export const PillCustomWrapper = styled.div<PillState>`
  display: inline-flex;
  flex-shrink: 0;

  [class*='PillWrapper'] {
    padding: 8px 11px;
    height: 40px;
    border-radius: ${({ theme }) => theme.kallisto.border.radius.radius400};
    transition: none;

    ${({ $loading }) =>
      $loading &&
      css`
        width: 102px;
      `}

    ${pillStateStyles}

    &:focus-visible {
      transition: none;
      border-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
      box-shadow: unset;
      ${focusRing};
    }

    [class*='Pill__IconWrapper'] {
      width: 20px;
      height: 20px;
      margin-right: 2px;

      svg,
      ${IconGlyph} {
        width: 20px;
        height: 20px;
      }
    }

    [class*='Pill__Label'] {
      overflow: visible;
      top: 0;
      margin-left: 0;
      span {
        ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
      }
    }
  }

  ${customStylesForSpecificFilterPills};
`;

export const pillTrailingSpace = css`
  ${PillCustomWrapper} {
    margin-right: ${({ theme }) => theme.kallisto.space.space100};
  }

  .last ${PillCustomWrapper} {
    margin-right: 0;
  }
`;

/** Keeps a skeletoned pill's name in the accessibility tree, out of sight. */
export const PillHiddenLabel = styled.span`
  ${srOnly};
`;
